import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartSchemaMapperConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import SchemaMapperConfigModal from 'processes/modals/field-mapping/SchemaMapperConfigModal';
import {MappableProcessType} from 'processes/ProcessConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ClientSchemaMapperConfig} from './FlowchartConfigConstants';

export interface ConfigureMapperButtonProps {
  schemaMapperConfig: ClientSchemaMapperConfig;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  allSchemasById: SchemaById;
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  isEditing: boolean;
  didInitialConfigHaveConditionalMapping: boolean;
  onSetMapperConfig: (mapperConfigSpec?: ProcessFlowchartSchemaMapperConfigSpec | null) => void;
  dataTestAttrs?: DataTestAttrs;
}

function getFromSchemaInfo(
  schemaMapperConfig: ClientSchemaMapperConfig,
  fromSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  rootTaskSchema: ProcessTaskSchemaGraphQL
): SchemaInfo {
  const fromSchemaIds = _.unique(
    (schemaMapperConfig.mapperConfig?.schemaFieldMapperConfigs ?? [])
      .map(({fromSchemaId}) => fromSchemaId)
      .filter(
        (fromSchemaId) => fromSchema.id === fromSchemaId || fromSchema.outputSchema?.id === fromSchemaId
      )
  );
  assert(fromSchemaIds.length <= 1, 'All mappers from a node must use the same source schema');

  let itemType: MappableProcessType;
  let canChangeItemType = fromSchema.outputSchema != null;
  if (fromSchemaIds.length === 0) {
    // If no field mappers exist using fromSchema we select a default itemType.
    if (fromSchema.id === rootTaskSchema.id) {
      canChangeItemType = false;
      itemType = ModelTypeConstants.PROCESS_TASK;
    } else {
      itemType =
        fromSchema.outputSchema != null ? ModelTypeConstants.PROCESS_OUTPUT : ModelTypeConstants.PROCESS_TASK;
    }
  } else {
    itemType = APIUtil.isProcessOutputSchema(fromSchemaIds[0])
      ? ModelTypeConstants.PROCESS_OUTPUT
      : ModelTypeConstants.PROCESS_TASK;
  }

  return {
    taskSchema: fromSchema,
    itemType,
    canChangeItemType,
  };
}

function getToSchemaInfo(
  schemaMapperConfig: ClientSchemaMapperConfig,
  toSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  rootTaskSchema: ProcessTaskSchemaGraphQL
): SchemaInfo {
  const toSchemaId = schemaMapperConfig.mapperConfig?.toSchemaId;

  let itemType: MappableProcessType;
  if (toSchemaId != null) {
    itemType = APIUtil.isProcessOutputSchema(toSchemaId)
      ? ModelTypeConstants.PROCESS_OUTPUT
      : ModelTypeConstants.PROCESS_TASK;
  } else if (toSchema.id === rootTaskSchema.id) {
    itemType = ModelTypeConstants.PROCESS_OUTPUT;
  } else {
    itemType = ModelTypeConstants.PROCESS_TASK;
  }

  return {
    taskSchema: toSchema,
    itemType,
  };
}

export default function ConfigureMapperButton(props: ConfigureMapperButtonProps): JSX.Element {
  const getButtonText = (): string => {
    if (props.isEditing) {
      return props.schemaMapperConfig.mapperConfig == null ? 'Configure' : 'Edit';
    }
    return 'View';
  };

  const buttonText = getButtonText();

  const getDisabledMessage = (): string | null => {
    if (props.schemaMapperConfig.fromTaskSchemas.length === 0) {
      return `Source schemas must be set to ${buttonText.toLocaleLowerCase()} mapper.`;
    } else if (props.schemaMapperConfig.toTaskSchema == null) {
      return `Destination schema must be set to ${buttonText.toLocaleLowerCase()} mapper.`;
    }
    return null;
  };

  const disabledMessage = getDisabledMessage();

  return (
    <TooltipWrapper tooltipTitle={disabledMessage}>
      <Button
        buttonStyle={ButtonStyle.LINK}
        className={css(styles.singleButton)}
        disabled={disabledMessage != null}
        text={buttonText}
        onClick={async () => {
          assert(
            props.schemaMapperConfig.fromTaskSchemas.length > 0 &&
              props.schemaMapperConfig.toTaskSchema != null,
            'Expected schema mapper config to be defined'
          );
          const fromSchemaInfos = props.schemaMapperConfig.fromTaskSchemas.map((taskSchema) =>
            getFromSchemaInfo(props.schemaMapperConfig, taskSchema, props.rootTaskSchema)
          );
          const toSchemaInfo = getToSchemaInfo(
            props.schemaMapperConfig,
            props.schemaMapperConfig.toTaskSchema,
            props.rootTaskSchema
          );
          const mapperConfig = await legacyRunModal(SchemaMapperConfigModal, {
            fromSchemaInfos,
            toSchemaInfo,
            flowchartTaskSchema: props.rootTaskSchema,
            schemaMapperConfig: props.schemaMapperConfig.mapperConfig,
            allSchemasById: props.allSchemasById,
            disabled: !props.isEditing,
            didInitialConfigHaveConditionalMapping: props.didInitialConfigHaveConditionalMapping,
          });
          props.onSetMapperConfig(mapperConfig);
        }}
        dataTestAttrs={props.dataTestAttrs}
      />
    </TooltipWrapper>
  );
}

const styles = StyleSheet.create({
  singleButton: {
    padding: '0px',
    boxShadow: 'none',
  },
});
